import { useCallback, useEffect, useRef, useState } from "react";
import { Circle, MessageSquare, Mic, MicOff, PhoneOff, Square, Video, VideoOff } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLive } from "@/lib/live";
import { CommentsPanel } from "./Comments";

export const chime = () => {
  try {
    const ac = new (window.AudioContext || window.webkitAudioContext)();
    [880, 660].forEach((f, i) => {
      const o = ac.createOscillator(); const g = ac.createGain();
      o.frequency.value = f; g.gain.setValueAtTime(0.12, ac.currentTime + i * 0.18); g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + i * 0.18 + 0.25);
      o.connect(g).connect(ac.destination); o.start(ac.currentTime + i * 0.18); o.stop(ac.currentTime + i * 0.18 + 0.26);
    });
    setTimeout(() => ac.close(), 800);
  } catch { /* no audio */ }
};
const detail = (e) => e?.response?.data?.detail?.message || errMsg(e);

let lastFail = 0;
// 409 reply and rec_failed push both arrive; show one toast.
const failToast = (m) => { if (Date.now() - lastFail < 8000) return; lastFail = Date.now(); toast.error(m, { id: "rec-failed" }); };

function RecordControl({ base, st, reload }) {
  const [open, setOpen] = useState(false);
  if (st.record_reason === "hidden") return null;
  const go = async (mode) => {
    setOpen(false);
    try { await api.post(`${base}/record`, { mode }); reload(); } catch (e) { failToast(detail(e)); reload(); }
  };
  if (st.recording) return st.can_stop ? <button className="flex min-h-[48px] items-center gap-2 rounded-full bg-white px-4 font-semibold text-black" onClick={() => api.post(`${base}/record/stop`).then(reload).catch((e) => toast.error(detail(e)))} data-testid="call-record-stop"><Square className="h-4 w-4" /> Stop</button> : null;
  return (
    <div className="relative">
      <button className="flex min-h-[48px] items-center gap-2 rounded-full bg-white/15 px-4 font-semibold text-white disabled:opacity-50" disabled={!st.can_record} onClick={() => setOpen(!open)} data-testid="call-record-btn"><Circle className="h-4 w-4 fill-red-500 text-red-500" /> Record</button>
      {!st.can_record && st.record_reason && <p className="absolute bottom-14 left-1/2 w-56 -translate-x-1/2 rounded-xl bg-black/80 p-2 text-center text-xs text-white" data-testid="call-record-reason">{st.record_reason}</p>}
      {open && <div className="absolute bottom-14 left-1/2 w-60 -translate-x-1/2 overflow-hidden rounded-2xl bg-zinc-900 text-white shadow-2xl" data-testid="call-record-menu">
        <button className="block min-h-[48px] w-full px-4 text-left hover:bg-white/10" onClick={() => go("video")} data-testid="call-record-video">Video + transcript</button>
        <button className="block min-h-[48px] w-full px-4 text-left hover:bg-white/10" onClick={() => go("audio")} data-testid="call-record-audio">Transcript only (audio)</button>
        <button className="block min-h-[48px] w-full px-4 text-left text-white/60 hover:bg-white/10" onClick={() => setOpen(false)} data-testid="call-record-cancel">Cancel</button>
      </div>}
    </div>
  );
}

function useLiveKit(url, token) {
  const [tiles, setTiles] = useState([]);
  const [failed, setFailed] = useState(false);
  const room = useRef(null);
  const grid = useRef(null);
  useEffect(() => {
    let r;
    (async () => {
      const lk = await import("livekit-client");
      r = new lk.Room({ adaptiveStream: true, dynacast: true });
      const sync = () => setTiles([r.localParticipant, ...Array.from(r.remoteParticipants.values())].slice(0, 12));
      r.on(lk.RoomEvent.TrackSubscribed, sync).on(lk.RoomEvent.ParticipantDisconnected, sync).on(lk.RoomEvent.ParticipantConnected, sync);
      await r.connect(url, token);
      await r.localParticipant.enableCameraAndMicrophone();
      room.current = r; sync();
    })().catch(() => setFailed(true));
    return () => { r?.disconnect(); };
  }, [url, token]);
  useEffect(() => {
    tiles.forEach((p) => p.videoTrackPublications?.forEach((pub) => {
      const el = grid.current?.querySelector(`[data-p="${p.identity}"] video`);
      if (pub.track && el) pub.track.attach(el);
    }));
  }, [tiles]);
  return { tiles, failed, room, grid };
}

export function CallRoom({ hub, call, onLeave }) {
  const base = `/organizations/${hub.id}/connect/calls/${call.id}`;
  const { subscribe } = useLive();
  const [st, setSt] = useState(null);
  const [panel, setPanel] = useState(false);
  const [mic, setMic] = useState(true);
  const [cam, setCam] = useState(true);
  const [note, setNote] = useState(null);
  const lk = useLiveKit(call.url, call.token);
  const load = useCallback(() => api.get(`${base}/state`).then(({ data }) => setSt(data)).catch(() => {}), [base]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => subscribe((m) => {
    if (m.entity !== "call" || m.payload?.call_id !== call.id) return;
    const p = m.payload;
    if (m.action === "joined" || m.action === "left") { toast(`${p.name} ${m.action}`, { id: `${m.action}-${p.by}` }); load(); }
    if (m.action === "rec_started") { setNote(p.message); chime(); load(); }
    if (m.action === "rec_stopped") { setNote(null); toast("Recording stopped"); load(); }
    if (m.action === "rec_failed") { failToast(p.message); load(); }
    if (m.action === "comment") setSt((s) => s && ({ ...s, comments: s.comments.some((c) => c.id === p.comment.id) ? s.comments : [...s.comments, p.comment] }));
    if (m.action === "comment_removed") setSt((s) => s && ({ ...s, comments: s.comments.map((c) => (c.id === p.comment_id ? { ...c, deleted: true, text: null } : c)) }));
    if (m.action === "ended") onLeave(false);
  }), [subscribe, call.id, load, onLeave]);
  const btn = "flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-white";
  return (
    <div className="fixed inset-0 z-[90] flex bg-black" data-testid="call-room">
      <div className="flex min-w-0 flex-1 flex-col">
        {st?.recording && <div className="flex flex-wrap items-center justify-center gap-x-3 bg-red-600 px-3 py-2 text-sm font-bold text-white" data-testid="call-recording-banner">
          <span>● Recording</span><span className="font-normal" data-testid="call-recording-by">{note || `${st.recording.by_name} started recording`}</span></div>}
        <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs text-white/70">
          <span className="truncate" data-testid="call-title">{st?.title}</span>
          <span data-testid="call-people-count">{st?.joined.length || 1} on the call</span>
        </div>
        <div ref={lk.grid} className={`grid flex-1 gap-1 p-1 ${lk.tiles.length > 4 ? "grid-cols-3" : lk.tiles.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
          {lk.failed ? <div className="flex flex-col items-center justify-center gap-2 text-center text-white/70" data-testid="call-video-unavailable">
            <VideoOff className="h-8 w-8" /><p>Video couldn't connect.</p><p className="text-xs">{st?.joined.map((j) => j.name.split(" ")[0]).join(", ")}</p></div>
            : lk.tiles.map((p) => <div key={p.identity} data-p={p.identity} className="relative overflow-hidden rounded-xl bg-zinc-900"><video autoPlay playsInline muted={p === lk.room.current?.localParticipant} className="h-full w-full object-cover" /><span className="absolute bottom-2 left-2 rounded bg-black/60 px-2 text-xs text-white">{p.name || p.identity}</span></div>)}
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3 p-4">
          <button className={btn} onClick={() => { lk.room.current?.localParticipant.setMicrophoneEnabled(!mic); setMic(!mic); }} aria-label="Microphone" data-testid="call-mic-btn">{mic ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}</button>
          <button className={btn} onClick={() => { lk.room.current?.localParticipant.setCameraEnabled(!cam); setCam(!cam); }} aria-label="Camera" data-testid="call-cam-btn">{cam ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}</button>
          {st && <RecordControl base={base} st={st} reload={load} />}
          <button className={`${btn} lg:hidden`} onClick={() => setPanel(true)} aria-label="Comments" data-testid="call-comments-btn"><MessageSquare className="h-5 w-5" /></button>
          <button className="flex h-12 w-12 items-center justify-center rounded-full bg-red-600 text-white" onClick={() => onLeave(true)} aria-label="Leave call" data-testid="call-hangup-btn"><PhoneOff className="h-5 w-5" /></button>
        </div>
      </div>
      {st && <CommentsPanel base={base} comments={st.comments} open={panel} onClose={() => setPanel(false)} meKey={st.me} parent={st.is_parent} />}
    </div>
  );
}
